/* eslint-disable @typescript-eslint/no-useless-constructor */
import React, { useState } from 'react'
import { Container, Row, Col } from 'reactstrap'
import axios from 'axios'
import ServeReportComponent from './ServeReportComponent'
import { useMsal, useAccount } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import './ReportComponents.css'
import { joinUrl } from '../utils/urlUtils'
import { getCachedBasicReports, updateCache, clearCache } from '../utils/reportCache'
import { ReportsResponse, ReportInfo } from '../types/reports'
import { getRuntimeConfig } from '../utils/configUtils'
import { acquireApiToken } from '../utils/authTokenUtils'
import type { AppInfo } from '../contexts/AppsContext'
import PageToolbar from './PageToolbar'

interface ReportComponentState {
  reportList: ReportInfo[]
  lastSync: string | null
  loading: boolean
  error: string | null
}

interface ReportComponentProps {
  app: AppInfo
  // Rendered above the toolbar, inside the page container (e.g. Additional Services sub-tabs)
  subNav?: React.ReactNode
}

const ReportComponent: React.FC<ReportComponentProps> = ({ app, subNav }) => {
  const reportType = app.key
  const reportTitle = `${app.label} Test Run`
  // Prefix, not substring: a short key like "hrApp" must not match another app's folder
  const reportPrefix = `TestRun-${app.reportPrefix}-`
  const { instance, inProgress } = useMsal()
  const account = useAccount()
  const [state, setState] = React.useState<ReportComponentState>({
    reportList: [],
    lastSync: null,
    loading: true,
    error: null,
  })
  const [hasInitialized, setHasInitialized] = React.useState(false)
  const [isRequesting, setIsRequesting] = React.useState(false)
  const [lastRequestTime, setLastRequestTime] = useState(0)
  const [requestQueue, setRequestQueue] = useState<Array<() => void>>([])
  const [isSyncing, setIsSyncing] = useState(false)

  const isRequestingRef = React.useRef(false)
  const lastRequestTimeRef = React.useRef(0)
  const stateRef = React.useRef(state)
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const isScheduledRef = React.useRef(false)

  React.useEffect(() => {
    isRequestingRef.current = isRequesting
  }, [isRequesting])

  React.useEffect(() => {
    lastRequestTimeRef.current = lastRequestTime
  }, [lastRequestTime])

  React.useEffect(() => {
    stateRef.current = state
  }, [state])

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const processRequestQueue = React.useCallback(async () => {
    if (requestQueue.length === 0 || isRequestingRef.current) {
      return
    }

    const nextRequest = requestQueue[0]
    setRequestQueue(prev => prev.slice(1))

    try {
      await nextRequest()
    } catch (error) {
      console.error('Error processing queued request:', error)
    }
  }, [requestQueue])

  React.useEffect(() => {
    if (!isRequestingRef.current && requestQueue.length > 0) {
      processRequestQueue()
    }
  }, [requestQueue.length, processRequestQueue])

  const fetchBasicReportList = React.useCallback(
    async (forceRefresh = false) => {
      try {
        if (!account) {
          throw new Error('No active account')
        }

        if (isRequestingRef.current) {
          setRequestQueue(prev => [...prev, () => fetchBasicReportList(forceRefresh)])
          return
        }

        const now = Date.now()
        if (now - lastRequestTimeRef.current < 1500) {
          setRequestQueue(prev => [...prev, () => fetchBasicReportList(forceRefresh)])
          return
        }

        setIsRequesting(true)
        setLastRequestTime(now)

        if (!forceRefresh) {
          const cachedData = getCachedBasicReports()
          if (cachedData && stateRef.current.reportList.length === 0) {
            setState(prev => ({
              ...prev,
              reportList: cachedData.reports,
              lastSync: cachedData.lastSync,
              loading: false,
            }))

            if (!isScheduledRef.current) {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
              }
              isScheduledRef.current = true
              timeoutRef.current = setTimeout(() => {
                isScheduledRef.current = false
                if (!isRequestingRef.current) {
                  fetchBasicReportList(true)
                }
              }, 5000)
            }
            return
          }
        }

        const accessToken = await acquireApiToken(instance, account)

        const apiUrl = getRuntimeConfig('REACT_APP_API_URL')

        if (!apiUrl) {
          throw new Error('API URL is not configured')
        }

        const requestUrl = joinUrl(apiUrl, '/api/reports')

        const apiResponse = await axios.get<ReportsResponse>(requestUrl, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          timeout: 15000,
        })

        console.log('Basic reports received, updating cache and showing...')
        if (apiResponse.data.reports[0]?.jsonReport) {
          if (apiResponse.data.reports[0].jsonReport.summary) {
            console.log('jsonReport.summary.runInfo:', (apiResponse.data.reports[0].jsonReport.summary as any).runInfo)
          }
        }

        updateCache(apiResponse.data)

        setState(prev => ({
          ...prev,
          reportList: apiResponse.data.reports,
          lastSync: apiResponse.data.lastSync,
          loading: false,
        }))
      } catch (error) {
        console.error('Error fetching basic reports:', error)

        const cachedData = getCachedBasicReports()
        if (cachedData && !forceRefresh) {
          console.log('Using cached data due to fetch error')
          setState(prev => ({
            ...prev,
            reportList: cachedData.reports,
            lastSync: cachedData.lastSync,
            loading: false,
          }))
        } else {
          setState(prev => ({
            ...prev,
            error: 'Failed to fetch reports',
            loading: false,
          }))
        }
      } finally {
        setIsRequesting(false)
      }
    },
    [account, instance],
  )

  React.useEffect(() => {
    if (account && inProgress === InteractionStatus.None && !hasInitialized) {
      setState(prev => ({
        ...prev,
        reportList: [],
        lastSync: null,
        loading: true,
        error: null,
      }))

      console.log('Fetching basic reports...')

      clearCache()

      setHasInitialized(true)

      setTimeout(() => {
        if (!isRequestingRef.current) {
          fetchBasicReportList(true)
        }
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, instance, inProgress, hasInitialized, reportType])

  React.useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    isScheduledRef.current = false

    setHasInitialized(false)
    setState(prev => ({
      ...prev,
      reportList: [],
      lastSync: null,
      loading: true,
      error: null,
    }))

    setRequestQueue([])
    setIsRequesting(false)
  }, [reportType])

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      isScheduledRef.current = false
      setRequestQueue([])
      setIsRequesting(false)
    }
  }, [])

  const manualSync = async () => {
    if (!account || isSyncing) return

    setIsSyncing(true)
    setState(prev => ({ ...prev, error: null }))

    try {
      const accessToken = await acquireApiToken(instance, account)

      const apiUrl = getRuntimeConfig('REACT_APP_API_URL')
      await axios.post(
        joinUrl(apiUrl, '/api/sync'),
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      )

      clearCache()
      await fetchBasicReportList(true)
    } catch (error) {
      console.error('Manual sync failed:', error)
      setState(prev => ({ ...prev, error: 'Sync failed. Please try again.' }))
    } finally {
      setIsSyncing(false)
    }
  }

  const downloadReportJson = async (report: ReportInfo) => {
    if (!account) return
    try {
      const accessToken = await acquireApiToken(instance, account)
      const apiUrl = getRuntimeConfig('REACT_APP_API_URL')
      const res = await fetch(joinUrl(apiUrl, `/api/reports/${report.name}/json`), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${report.name}.json`
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download report JSON:', error)
    }
  }

  const formatReportNameFallback = (filename: string, timestamp: string): string | null => {
    const dateMatch = filename.match(/(\d{1,2}-\d{1,2}-\d{4})/)
    if (!dateMatch) {
      console.log(`No date match found in ${filename}`)
      return null
    }

    const date = new Date(timestamp)
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const formattedHours = hours % 12 || 12
    const formattedTime = `${formattedHours}:${minutes.toString().padStart(2, '0')} ${ampm}`

    const result = `${dateMatch[1]} ${formattedTime}`
    return result
  }
  const generateReportTitle = (report: ReportInfo, fallbackTitle: string): string => {
    const runInfo = report.jsonReport?.runInfo || (report.jsonReport?.summary as any)?.runInfo
    if (!report.hasJsonReport || !runInfo) return fallbackTitle
    const testModeLabel =
      runInfo.testMode === 'sanity' ? ' (Sanity)' : runInfo.testMode === 'load' ? ' (Load Test)' : ''
    if (runInfo.source === 'repository_dispatch') {
      const rawRepo = runInfo.payload?.repo || runInfo.repository
      const repoName = rawRepo ? rawRepo.split('/').pop() : null
      const prNumber = runInfo.payload?.prNumber
      if (repoName && prNumber) return `${repoName} PR #${prNumber}${testModeLabel}`
      if (repoName) return `${repoName} CI Run${testModeLabel}`
      return `CI Test Run${testModeLabel}`
    }
    if (runInfo.source === 'local') return `Local Test Run${testModeLabel}`
    return fallbackTitle
  }

  const generateReportSubtitle = (report: ReportInfo): string | null => {
    const runInfo = report.jsonReport?.runInfo || (report.jsonReport?.summary as any)?.runInfo
    if (!runInfo || runInfo.source !== 'repository_dispatch') return null
    const rawRepo = runInfo.payload?.repo || runInfo.repository
    const repoName = rawRepo ? rawRepo.split('/').pop() : null
    let triggerPart: string
    if (runInfo.payload?.ref) {
      const branchName = runInfo.payload.ref.replace('refs/heads/', '').split('/').pop()
      const sha: string | undefined = runInfo.payload.sha
      triggerPart = `"${branchName}"${sha ? ` at ${sha}` : ''}`
    } else {
      triggerPart = 'CI Trigger'
    }
    return repoName ? `${repoName} · ${triggerPart}` : triggerPart
  }
  const generateCommitUrl = (report: ReportInfo): string | null => {
    const runInfo = report.jsonReport?.runInfo || (report.jsonReport?.summary as any)?.runInfo
    if (!runInfo || runInfo.source !== 'repository_dispatch') return null
    if (runInfo.payload?.prUrl) return runInfo.payload.prUrl
    const repo = runInfo.payload?.repo || runInfo.repository
    const sha = runInfo.payload?.sha
    return repo && sha ? `https://github.com/${repo}/commit/${sha}` : null
  }
  const generateReportName = (report: ReportInfo): string | null => {
    return formatReportNameFallback(report.name, report.lastUpdated)
  }

  const RenderTestStats = ({ report }: { report: ReportInfo & { formatted: string | null } }) => {
    if (!report.hasJsonReport || !report.formatted) return null

    if (!report.jsonReport) {
      return (
        <div className="test-stats-container">
          <div className="stats-loading">Loading stats...</div>
        </div>
      )
    }

    const stats = report.jsonReport.stats
    const testRunStats = report.jsonReport.testRunStats || ({} as any)
    const summary = report.jsonReport.summary || ({} as any)

    const expected =
      stats?.expected !== undefined
        ? stats.expected
        : testRunStats.expected !== undefined
          ? testRunStats.expected
          : summary.passed !== undefined
            ? summary.passed
            : 0
    const unexpected =
      stats?.unexpected !== undefined
        ? stats.unexpected
        : testRunStats.unexpected !== undefined
          ? testRunStats.unexpected
          : summary.failed !== undefined
            ? summary.failed
            : 0
    const flaky =
      stats?.flaky !== undefined
        ? stats.flaky
        : testRunStats.flaky !== undefined
          ? testRunStats.flaky
          : summary.flaky !== undefined
            ? summary.flaky
            : 0
    const skipped =
      stats?.skipped !== undefined
        ? stats.skipped
        : testRunStats.skipped !== undefined
          ? testRunStats.skipped
          : summary.skipped !== undefined
            ? summary.skipped
            : 0

    const durationMs = stats?.duration
    const durationStr = durationMs
      ? `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000) / 1000)}s`
      : null
    const fileId = report.name.split('-').pop()

    return (
      <div className="test-stats-container">
        <div className="test-stats">
          <div className="stat-item pass">
            <span className="stat-icon">✓</span>
            <span className="stat-label">Pass:</span>
            <span className="stat-value">{expected}</span>
          </div>
          <div className="stat-item fail">
            <span className="stat-icon">✗</span>
            <span className="stat-label">Fail:</span>
            <span className="stat-value">{unexpected}</span>
          </div>
          <div className="stat-item flaky">
            <span className="stat-icon">⚠</span>
            <span className="stat-label">Flaky:</span>
            <span className="stat-value">{flaky}</span>
          </div>
          <div className="stat-item skipped">
            <span className="stat-icon">⏭</span>
            <span className="stat-label">Skip:</span>
            <span className="stat-value">{skipped}</span>
          </div>
        </div>
        <div className="report-meta-info">
          {durationStr && <span className="meta-duration">⏱ {durationStr}</span>}
          {fileId && <span className="meta-id">ID: {fileId}</span>}
        </div>
      </div>
    )
  }

  const RenderReportList = () => {
    if (state.loading) {
      return (
        <div className="loading-spinner">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading reports...</span>
          </div>
        </div>
      )
    }

    if (state.error) {
      return <div className="error-message">{state.error}</div>
    }

    const sortedReports = [...state.reportList]
      .filter(report => report.name.startsWith(reportPrefix))
      .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
      .map(report => ({
        ...report,
        formatted: generateReportName(report),
      }))
      .filter(report => report.formatted !== null)

    if (sortedReports.length === 0) {
      return <div className="empty-state">No {app.label} reports yet.</div>
    }

    return (
      <Row>
        <Col>
          {sortedReports.map(report => {
            const subtitle = generateReportSubtitle(report)
            const commitUrl = generateCommitUrl(report)
            return (
              <Row key={report.name} className="report-row">
                <Col xs={12} className="report-card-row">
                  <div className="report-date">
                    <div className="report-title">{generateReportTitle(report, reportTitle)}</div>
                    {subtitle && (
                      <div className="report-subtitle">
                        {commitUrl ? (
                          <a
                            href={commitUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="report-commit-link"
                            onClick={e => e.stopPropagation()}
                          >
                            {subtitle}
                          </a>
                        ) : (
                          subtitle
                        )}
                      </div>
                    )}
                    {report.formatted && (
                      <div className="report-timestamp">
                        <span>{report.formatted}</span>
                      </div>
                    )}
                    {report.hasJsonReport && <RenderTestStats report={report} />}
                  </div>
                  <div className="report-buttons-right" onClick={e => e.stopPropagation()}>
                    {report.hasJsonReport && (
                      <>
                        <a
                          href={`/report/${report.name}/custom`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="details-link-btn"
                        >
                          Details...
                        </a>
                        <button className="details-link-btn" onClick={() => downloadReportJson(report)}>
                          Download JSON
                        </button>
                      </>
                    )}
                    {report.hasPlaywright && <ServeReportComponent reportName={report.name} reportType="playwright" />}
                    {report.hasMonocart && <ServeReportComponent reportName={report.name} reportType="monocart" />}
                  </div>
                </Col>
              </Row>
            )
          })}
        </Col>
      </Row>
    )
  }

  return (
    <Container className="report-container">
      {subNav}
      <PageToolbar
        lastSync={state.lastSync}
        onSync={manualSync}
        syncing={isSyncing}
        disabled={state.loading}
        syncLabel="Sync with S3"
      />
      {RenderReportList()}
    </Container>
  )
}

export default ReportComponent
